import React, { useState, useRef, useEffect } from "react";
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

export default function CustomTemplateForm({ onGenerate, onDelete, initialData }) {
    const toast = useRef(null);
    const [delimiter, setDelimiter] = useState('');
    const [label, setLabel] = useState('');
    const [textPosition, setTextPosition] = useState('');
    const [items, setItems] = useState([]);
    const [editIndex, setEditIndex] = useState(null);

    // Load initial data if passed
    useEffect(() => {
        if (initialData) {
            setDelimiter(initialData.delimiter || '');
            setItems(initialData.fields || []);
            // } else {
            //     console.log('else')
            //     // default state
            //     setDelimiter('');
            //     setItems([]);
        }
    }, [initialData]);

    const saveItem = () => {
        var labelTest = /^[^'"]*$/.test(label);
        if (!labelTest) {
            toast.current.show({ severity: 'error', summary: 'Validation', detail: "Label should not contain single (') or double (\") quotes." })
            return;
        }
        if (!delimiter.trim()) {
            toast.current.show({ severity: 'error', summary: 'Validation', detail: 'Delimiter Position is required.' });
            return;
        }
        if (!textPosition.trim()) {
            toast.current.show({ severity: 'error', summary: 'Validation', detail: 'Text Position is required.' });
            return;
        }
        if (isNaN(textPosition)) {
            toast.current.show({ severity: 'error', summary: 'Validation', detail: 'Text Position must be a number.' });
            return;
        }
        // const duplicate = items.some(
        //     (item, idx) => item.label.toLowerCase() === label.toLowerCase() && idx !== editIndex
        // );
        // if (duplicate) {
        //     toast.current.show({ severity: 'error', summary: 'Validation', detail: 'Label already exists.' });
        //     return;
        // }
        const lbl = label.trim() || "  ";
        const pos = textPosition.trim();
        const newItem = { label: lbl, textPosition: pos };
        if (editIndex !== null) {
            const updated = [...items];
            updated[editIndex] = newItem;
            setItems(updated);
            setEditIndex(null);
        } else {
            setItems([...items, newItem]);
        }
        setLabel('');
        setTextPosition('');
    };

    const editItem = (index) => {
        setLabel(items[index].label);
        setTextPosition(items[index].textPosition);
        setEditIndex(index);
    };

    const deleteItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const onDragEnd = (result) => {
        if (!result.destination) return; // dropped outside list
        const reorderedItems = Array.from(items);
        const [movedItem] = reorderedItems.splice(result.source.index, 1);
        reorderedItems.splice(result.destination.index, 0, movedItem);
        setItems(reorderedItems);
    };

    const handleGenerate = () => {
        const payload = {
            delimiter,
            fields: items,
        };
        if (onGenerate) onGenerate(payload);
    };

    const handleDelete = () => {
        if (onDelete) onDelete(null);
    };

    const handleDelimiterChange = (e) => {
        const val = e.target.value;
        if (/^[,;:^|]?$/.test(val)) {
            setDelimiter(val);
        }
    };

    return (
        <div>
            {/* Instructions */}
            <div className="p-4 max-w-2xl mx-auto">
                <h6>Instructions</h6>
                <ul>
                    <li>Make sure to select the correct delimiter ( , ; : ^ | ) as per your event log text.</li>
                    <li>Use a label to name the segment after splitting the log text; assign the position of the segment present in the log.</li>
                    <li>Set Text Position to <strong>-1</strong> to add a normal or static line of text.</li>
                    <li>Set Label to empty & Text Position to <strong>-1</strong> to add a blank line.</li>
                </ul>

                {/* Delimiter */}
                <div className="mb-3">
                    <label className="block mb-1 font-medium required-field">Delimiter</label>
                    <InputText value={delimiter} onChange={handleDelimiterChange} placeholder="Enter delimiter ( , ; : ^ | )" maxLength={1} />
                </div>

                {/* Label & Text Position Inputs */}
                <div className="flex flex-wrap gap-2 mb-3 items-center">
                    <div className="flex-1">
                        <label className="block mb-1 font-medium">Label</label>
                        <InputText value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" />
                    </div>
                    <div className="flex-1">
                        <label className="block mb-1 font-medium required-field">Text Position</label>
                        <InputText value={textPosition} onChange={(e) => setTextPosition(e.target.value)} placeholder="Text Position (number)" />
                    </div>
                    <div className="flex align-items-center pt-4">
                        <Button label={editIndex !== null ? "Update" : "Add"} icon={editIndex !== null ? "pi pi-pencil" : "pi pi-plus"} onClick={saveItem} severity="primary" />
                    </div>
                </div>

                <Toast ref={toast} />

                {/* Drag-and-Drop List */}
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className="mb-4">
                                {items.map((item, index) => (
                                    <Draggable key={index} draggableId={`item-${index}`} index={index}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="flex justify-between items-center mb-2 p-2 hover:bg-gray-100 rounded">
                                                <div>
                                                    <b>{item.label}</b> → Position: {item.textPosition}
                                                </div>
                                                <div className="">
                                                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-info" onClick={() => editItem(index)} tooltip="Edit" />
                                                    <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger" onClick={() => deleteItem(index)} tooltip="Delete" />
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                <Button icon="pi pi-save" label="Save" onClick={handleGenerate} tooltip="Save template" /> &nbsp;
                {delimiter ? <>
                    <Button icon="pi pi-trash" label="Delete" onClick={handleDelete} className="p-button-danger" tooltip="Delete template" />
                </> : <></>}
            </div>
        </div>
    );
}
